import { db } from "./index.js";
import { DebugLogger } from "./debugLogger.js";
import { isQuotaError, reportQuotaExceeded } from "./quotaSignal.js";

/**
 * File Storage Module (Mobile-Only)
 *
 * Handles local file storage operations for mobile sanitation routes:
 * - File storage/retrieval using IndexedDB
 * - iOS Safari base64 compatibility
 * - Image optimization for mobile
 * - Basic cleanup utilities
 *
 * For upload-specific file operations, see fileUpload.js
 */

// Generate unique ID for files
export function generateFileId() {
  return "file_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
}

// Store file locally using IndexedDB (mobile-only)
export async function storeFileLocally(file, fileId = null) {
  const id = fileId || generateFileId();

  try {
    const result = await storeWithIndexedDB(file, id);
    return result;
  } catch (error) {
    DebugLogger.error("Failed to store file locally", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      error: error.message,
      isIPhone: navigator.userAgent.includes("iPhone"),
    });
    throw new Error(`Failed to store file locally: ${error.message}`);
  }
}

// Store file using IndexedDB blob storage
async function storeWithIndexedDB(file, fileId) {
  // Check available storage before attempting to store
  const storageInfo = await getStorageInfo();
  if (storageInfo && storageInfo.available < file.size * 2) {
    // 2x size for safety margin
    throw new Error(
      `Insufficient storage space. Available: ${Math.round(
        storageInfo.available / 1024 / 1024
      )}MB, Required: ${Math.round((file.size * 2) / 1024 / 1024)}MB`
    );
  }

  const currentTimestamp = new Date().toISOString();
  const isIOS = navigator.userAgent.includes("iPhone");

  try {
    let fileRecord;

    if (isIOS) {
      // For iOS Safari: Store as base64 string to avoid blob issues
      DebugLogger.info("Converting file to base64 for iOS Safari storage", {
        fileId,
        fileName: file.name,
        fileSize: file.size,
      });

      const base64Data = await fileToBase64Direct(file);

      fileRecord = {
        file_id: fileId,
        blob: null, // No blob storage for iOS
        base64_data: base64Data, // Store base64 directly
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        created_at: currentTimestamp,
        last_accessed: currentTimestamp,
        storage_method: "base64", // Mark as base64 storage
      };

      DebugLogger.info("File stored as base64 for iOS Safari", {
        fileId,
        fileName: file.name,
        base64Length: base64Data.length,
      });
    } else {
      // For other browsers: Use blob storage as before
      const blob = new Blob([file], { type: file.type });

      fileRecord = {
        file_id: fileId,
        blob: blob,
        base64_data: null,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        created_at: currentTimestamp,
        last_accessed: currentTimestamp,
        storage_method: "blob",
      };

      DebugLogger.info("Storing file record to IndexedDB", {
        fileId,
        fileName: file.name,
        fileSize: file.size,
        blobSize: blob.size,
        fileType: file.type,
      });
    }

    await db.fileBlobs.put(fileRecord);

    DebugLogger.info("File stored successfully in IndexedDB", {
      fileId,
      fileName: file.name,
      localPath: `indexeddb://${fileId}`,
      storageMethod: fileRecord.storage_method,
    });

    return {
      fileId: fileId,
      localPath: `indexeddb://${fileId}`,
      size: file.size,
      type: file.type,
      name: file.name,
      storageMethod: fileRecord.storage_method,
    };
  } catch (error) {
    // Check if it's a quota error
    if (isQuotaError(error)) {
      reportQuotaExceeded();
      DebugLogger.error("Storage quota exceeded", {
        fileName: file.name,
        fileSize: file.size,
        error: error.message,
      });
      throw new Error(
        `Storage quota exceeded. Try deleting some files or clear browser storage.`
      );
    }

    DebugLogger.error("IndexedDB storage failed", {
      fileName: file.name,
      fileSize: file.size,
      error: error.message,
    });
    throw error;
  }
}

// Direct base64 conversion for storage (separate from the upload conversion)
function fileToBase64Direct(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      const errorMessage =
        error?.target?.error?.message ||
        "FileReader error during storage conversion";
      DebugLogger.error("FileReader error during storage", {
        fileName: file.name,
        error: errorMessage,
      });
      reject(new Error(`FileReader error during storage: ${errorMessage}`));
    };

    reader.readAsDataURL(file);
  });
}

// Retrieve file from local storage (IndexedDB only)
export async function getLocalFile(localPath) {
  try {
    DebugLogger.info("Getting local file", { localPath });

    if (localPath.startsWith("indexeddb://")) {
      return await getFileFromIndexedDB(localPath);
    }

    DebugLogger.error("Unsupported storage method", { localPath });
    throw new Error(`Unsupported storage method: ${localPath}`);
  } catch (error) {
    DebugLogger.error("Local file retrieval failed", {
      localPath,
      error: error.message,
      errorName: error.name,
      stack: error.stack,
    });

    // Ensure we pass the actual error message, not undefined
    const errorMessage =
      error.message ||
      error.toString() ||
      "Unknown error during file retrieval";
    throw new Error(`Failed to retrieve local file: ${errorMessage}`);
  }
}

// Retrieve file from IndexedDB blob storage
async function getFileFromIndexedDB(localPath) {
  const fileId = localPath.replace("indexeddb://", "");

  try {
    DebugLogger.info("Retrieving file from IndexedDB", {
      fileId,
      localPath,
    });

    // Check if database is available
    if (!db || !db.fileBlobs) {
      DebugLogger.error("IndexedDB database not available", {
        hasDb: !!db,
        hasFileBlobs: !!(db && db.fileBlobs),
      });
      throw new Error("IndexedDB database not available");
    }

    let fileRecord;
    try {
      fileRecord = await db.fileBlobs.get(fileId);
    } catch (dbError) {
      DebugLogger.error("IndexedDB get operation failed", {
        fileId,
        dbError: dbError.message || dbError.toString(),
        dbErrorName: dbError.name,
        isIOS: navigator.userAgent.includes("iPhone"),
      });
      throw new Error(
        `Database access error: ${dbError.message || dbError.toString()}`
      );
    }
    if (!fileRecord) {
      DebugLogger.error("File record not found in IndexedDB", {
        fileId,
        localPath,
      });
      throw new Error(`File not found in IndexedDB: ${fileId}`);
    }

    DebugLogger.info("File record found", {
      fileId,
      fileName: fileRecord.file_name,
      fileSize: fileRecord.file_size,
      hasBlob: !!fileRecord.blob,
      blobSize: fileRecord.blob?.size,
    });

    // Update last accessed timestamp
    try {
      await db.fileBlobs.update(fileId, {
        last_accessed: new Date().toISOString(),
      });
    } catch (updateError) {
      DebugLogger.warn("Failed to update last_accessed timestamp", {
        fileId,
        updateError: updateError.message || updateError.toString(),
      });
      // Don't throw here, just warn - this shouldn't block file retrieval
    }

    // Handle different storage methods
    const storageMethod = fileRecord.storage_method || "blob";

    if (storageMethod === "base64") {
      // iOS Safari: File was stored as base64, return it directly
      DebugLogger.info("Retrieving base64-stored file for iOS Safari", {
        fileId,
        fileName: fileRecord.file_name,
        base64Length: fileRecord.base64_data?.length,
      });

      if (!fileRecord.base64_data) {
        DebugLogger.error("Base64 data missing for iOS Safari file", {
          fileId,
          fileName: fileRecord.file_name,
        });
        throw new Error(`Base64 data missing in IndexedDB for: ${fileId}`);
      }

      // Create a simple object that works with fileToBase64 function
      const base64FileObject = {
        name: fileRecord.file_name,
        size: fileRecord.file_size,
        type: fileRecord.file_type,
        lastModified: new Date(fileRecord.created_at).getTime(),
        // Special property to indicate this already has base64 data
        _base64Data: fileRecord.base64_data,
      };

      DebugLogger.info("Base64 file object created for iOS Safari", {
        fileId,
        fileName: base64FileObject.name,
        hasBase64Data: !!base64FileObject._base64Data,
      });

      return base64FileObject;
    }

    // Regular blob storage for non-iOS browsers
    if (!fileRecord.blob) {
      DebugLogger.error("File record exists but blob is missing", {
        fileId,
        fileName: fileRecord.file_name,
        fileSize: fileRecord.file_size,
      });
      throw new Error(`File blob is missing in IndexedDB for: ${fileId}`);
    }

    // Return File object from stored blob - iOS Safari specific handling
    try {
      // Check if blob is actually a Blob object
      if (!(fileRecord.blob instanceof Blob)) {
        DebugLogger.error("Stored data is not a Blob object", {
          fileId,
          blobType: typeof fileRecord.blob,
          blobConstructor: fileRecord.blob?.constructor?.name,
        });
        throw new Error(`Stored data is not a Blob object for file: ${fileId}`);
      }

      // iOS Safari Fix: Create a completely fresh Blob from ArrayBuffer
      // FileReader on iPhone Safari has issues with any blobs from IndexedDB
      const isIOS = navigator.userAgent.includes("iPhone");

      if (isIOS) {
        DebugLogger.info(
          "Creating ArrayBuffer-based Blob for iOS Safari compatibility",
          {
            fileId,
            originalBlobSize: fileRecord.blob.size,
            fileName: fileRecord.file_name,
          }
        );

        try {
          // Read the blob as ArrayBuffer first to completely disconnect from IndexedDB
          const arrayBuffer = await fileRecord.blob.arrayBuffer();

          DebugLogger.info("ArrayBuffer created from blob", {
            fileId,
            arrayBufferSize: arrayBuffer.byteLength,
          });

          // Create a completely fresh Blob from the ArrayBuffer
          const freshBlob = new Blob([arrayBuffer], {
            type: fileRecord.file_type,
          });

          // Add file metadata as properties
          freshBlob.name = fileRecord.file_name;
          freshBlob.lastModified = new Date(fileRecord.created_at).getTime();

          DebugLogger.info(
            "Fresh ArrayBuffer-based Blob created for iOS Safari",
            {
              fileId,
              blobSize: freshBlob.size,
              blobType: freshBlob.type,
              fileName: freshBlob.name,
              arrayBufferSize: arrayBuffer.byteLength,
            }
          );

          return freshBlob;
        } catch (arrayBufferError) {
          DebugLogger.error("Failed to create ArrayBuffer from blob", {
            fileId,
            error: arrayBufferError.message,
          });
          // Fallback to original blob if ArrayBuffer approach fails
          return fileRecord.blob;
        }
      } else {
        // For non-iOS browsers, use File object as before
        const file = new File([fileRecord.blob], fileRecord.file_name, {
          type: fileRecord.file_type,
          lastModified: new Date(fileRecord.created_at).getTime(),
        });

        // Verify the File object was created correctly
        if (!file || !file.name || file.size === undefined) {
          DebugLogger.error("File object creation failed", {
            fileId,
            hasFile: !!file,
            fileName: file?.name,
            fileSize: file?.size,
          });
          throw new Error(`File object creation failed for: ${fileId}`);
        }

        DebugLogger.info("File object created successfully", {
          fileId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });

        return file;
      }
    } catch (fileCreationError) {
      DebugLogger.error("File object creation error", {
        fileId,
        fileName: fileRecord.file_name,
        blobSize: fileRecord.blob?.size,
        error: fileCreationError.message,
        isIOS: navigator.userAgent.includes("iPhone"),
      });
      throw fileCreationError;
    }
  } catch (error) {
    DebugLogger.error("IndexedDB file retrieval failed", {
      fileId,
      localPath,
      error: error.message,
      errorName: error.name,
      stack: error.stack,
    });
    throw error;
  }
}

// Convert file to base64 for upload
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    // Validate input file
    if (!file) {
      DebugLogger.error("fileToBase64: No file provided");
      reject(new Error("No file provided for base64 conversion"));
      return;
    }

    // Check if this is a special base64 file object (iOS Safari)
    if (file._base64Data) {
      DebugLogger.info("Using pre-stored base64 data for iOS Safari", {
        fileName: file.name || "unnamed",
        fileSize: file.size,
        base64Length: file._base64Data.length,
      });
      resolve(file._base64Data);
      return;
    }

    if (!(file instanceof File) && !(file instanceof Blob)) {
      DebugLogger.error("fileToBase64: Invalid file type", {
        fileType: typeof file,
        constructor: file?.constructor?.name,
        isFile: file instanceof File,
        isBlob: file instanceof Blob,
        hasBase64Data: !!file._base64Data,
      });
      reject(
        new Error(`Invalid file type for base64 conversion: ${typeof file}`)
      );
      return;
    }

    DebugLogger.info("Starting base64 conversion", {
      fileName: file.name || "unnamed",
      fileSize: file.size,
      fileType: file.type || "unknown",
      isFile: file instanceof File,
      isBlob: file instanceof Blob,
      isIOS: navigator.userAgent.includes("iPhone"),
    });

    const reader = new FileReader();

    reader.onload = () => {
      if (!reader.result) {
        DebugLogger.error("FileReader result is empty");
        reject(new Error("FileReader produced empty result"));
        return;
      }

      DebugLogger.info("Base64 conversion successful", {
        fileName: file.name || "unnamed",
        resultLength: reader.result.length,
        resultType: typeof reader.result,
      });

      resolve(reader.result);
    };

    reader.onerror = (error) => {
      const errorMessage =
        error?.target?.error?.message ||
        error?.message ||
        "FileReader error during base64 conversion";

      DebugLogger.error("FileReader error", {
        fileName: file.name || "unnamed",
        fileSize: file.size,
        error: errorMessage,
        errorCode: error?.target?.error?.code,
        isIOS: navigator.userAgent.includes("iPhone"),
      });

      reject(new Error(`FileReader error: ${errorMessage}`));
    };

    reader.onabort = () => {
      DebugLogger.error("FileReader aborted", {
        fileName: file.name || "unnamed",
      });
      reject(new Error("FileReader operation was aborted"));
    };

    try {
      reader.readAsDataURL(file);
    } catch (error) {
      DebugLogger.error("FileReader readAsDataURL failed", {
        fileName: file.name || "unnamed",
        error: error.message || error.toString(),
      });
      reject(
        new Error(
          `Failed to start FileReader: ${error.message || error.toString()}`
        )
      );
    }
  });
}

// Clean up local files after successful upload (IndexedDB only)
export async function deleteLocalFile(localPath) {
  try {
    if (localPath.startsWith("indexeddb://")) {
      return await deleteFileFromIndexedDB(localPath);
    }

    console.warn(`Unknown storage method for cleanup: ${localPath}`);
    return false;
  } catch (error) {
    console.error("Error deleting local file:", error);
    return false;
  }
}

// Delete file from IndexedDB storage
async function deleteFileFromIndexedDB(localPath) {
  const fileId = localPath.replace("indexeddb://", "");

  await db.fileBlobs.delete(fileId);
  console.log(`Deleted file from IndexedDB: ${fileId}`);
  return true;
}

// Storage management functions
export async function getStorageInfo() {
  try {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        quota: estimate.quota,
        usage: estimate.usage,
        available: estimate.quota - estimate.usage,
        usagePercent: Math.round((estimate.usage / estimate.quota) * 100),
      };
    }
    return null;
  } catch (error) {
    console.error("Error getting storage info:", error);
    return null;
  }
}

// Clean up old files based on last access time
export async function cleanupOldFiles(maxAgeInDays = 7) {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - maxAgeInDays);
    const cutoffTimestamp = cutoffDate.toISOString();
    const protectedFileIds = new Set(
      (await db.fileQueue.toArray())
        .filter((entry) => entry.upload_status !== "completed")
        .map((entry) => entry.file_local_id)
    );

    const oldFiles = await db.fileBlobs
      .where("last_accessed")
      .below(cutoffTimestamp)
      .and((fileRecord) => !protectedFileIds.has(fileRecord.file_id))
      .toArray();

    let deletedCount = 0;
    for (const fileRecord of oldFiles) {
      const localPath = `indexeddb://${fileRecord.file_id}`;
      const success = await deleteLocalFile(localPath);
      if (success) deletedCount++;
    }

    console.log(`Cleaned up ${deletedCount} old files`);
    return deletedCount;
  } catch (error) {
    console.error("Error cleaning up old files:", error);
    return 0;
  }
}

// Get all stored files info
export async function getStoredFilesInfo() {
  try {
    const files = await db.fileBlobs.toArray();
    const totalSize = files.reduce((sum, file) => sum + file.file_size, 0);

    return {
      count: files.length,
      totalSize: totalSize,
      files: files.map((file) => ({
        fileId: file.file_id,
        name: file.file_name,
        size: file.file_size,
        type: file.file_type,
        createdAt: file.created_at,
        lastAccessed: file.last_accessed,
        storageMethod: file.storage_method || "indexeddb",
      })),
    };
  } catch (error) {
    console.error("Error getting stored files info:", error);
    return { count: 0, totalSize: 0, files: [] };
  }
}

// Optimize image files for upload (mobile-optimized)
export async function optimizeImageFile(
  file,
  maxWidth = 1280,
  maxHeight = 720,
  quality = 0.7
) {
  return new Promise((resolve) => {
    // Only optimize images
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    // Set timeout for image loading (mobile-optimized)
    const timeoutId = setTimeout(() => {
      DebugLogger.warn("Image optimization timeout", { fileName: file.name });
      resolve(file);
    }, 30000);

    img.onload = () => {
      clearTimeout(timeoutId);

      try {
        // Calculate optimal dimensions
        let { width, height } = calculateOptimalDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              DebugLogger.warn("Failed to create blob during optimization", {
                fileName: file.name,
              });
              resolve(file);
              return;
            }

            // Preserve original file name and metadata
            const optimizedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });

            resolve(optimizedFile);
          },
          "image/jpeg",
          quality
        );
      } catch (error) {
        clearTimeout(timeoutId);
        DebugLogger.error("Error during image optimization", {
          fileName: file.name,
          error: error.message,
        });
        resolve(file); // Fallback to original file
      }
    };

    img.onerror = (error) => {
      clearTimeout(timeoutId);
      DebugLogger.error("Error loading image for optimization", {
        fileName: file.name,
        error: error?.message || "Unknown error",
      });
      resolve(file); // Fallback to original file
    };

    try {
      img.src = URL.createObjectURL(file);
    } catch (error) {
      clearTimeout(timeoutId);
      DebugLogger.error("Error creating object URL for optimization", {
        fileName: file.name,
        error: error.message,
      });
      resolve(file); // Fallback to original file
    }
  });
}

// Calculate optimal dimensions maintaining aspect ratio
function calculateOptimalDimensions(
  originalWidth,
  originalHeight,
  maxWidth,
  maxHeight
) {
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }

  const aspectRatio = originalWidth / originalHeight;

  let width = maxWidth;
  let height = maxWidth / aspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = maxHeight * aspectRatio;
  }

  return { width: Math.round(width), height: Math.round(height) };
}
